// e2e/Detail.tsx
import { useQuery } from '@tanstack/react-query'
import React, { MutableRefObject, useEffect, useRef, useState } from 'react'
import toast, { Toaster } from 'react-hot-toast'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { formatCashAmount, isInterface } from '../../../../../lib/utils'
import { Breadcrumb } from '../../../../../components/ui/breadcrumb'
import errorAnimation from '../../../../../assets/animations/error.json'
import lottie from 'lottie-web'
import axios from '../../../../../lib/axios'
import useAuth from '../../../../../hooks/useAuth'
import { convertToDate, formatDateToYYYYMMDD } from '../../../../../lib/date'
import { addDays, format } from 'date-fns'
import { useSelector } from 'react-redux'
import { RootState } from '../../../../../redux'
import { Tablist, TablistItem } from '../../../../../components/ui/tablist'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../../../components/ui/table'
import { Button } from '../../../../../components/ui/button'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import LoadingScreen from '../../../../../components/portal/feedback/LoadingScreen'
import { useTranslation } from 'react-i18next'

type E2EDetailRow = {
  type: string
  keepsafe: string
  eur5: number
  eur10: number
  eur20: number
  eur50: number
  eur100: number
  eur200: number
  eur500: number
  total: number
}

const TYPE_ORDER = [
  'A_SwapIn',
  'E_SwapOut',
  'D_Retract',
  'B_Counters',
  'C_RejectNOK',
  'J_RejectNok',
  'I_Remaining',
  'F_New Amount',
]

const Detail = () => {
  const { clientcode, date, relnr } = useParams()
  const { t } = useTranslation()
  const { auth } = useAuth()
  const navigate = useNavigate()
  const partners = useSelector((state: RootState) => state.partners)
  const customers = useSelector((state: RootState) => state.unfilteredCustomers)

  const errorContainer = useRef() as MutableRefObject<HTMLDivElement>

  const [selected_subpage, setselected_subpage] = useState('d')
  const [selected_document, setselected_document] = useState(0)

  const { data: detail, isFetching } = useQuery<any[] | null>({
    queryKey: [`e2eservice_${clientcode}_${date}_${relnr}`],
    queryFn: async () => {
      try {
        const params: any = {
          token: auth.data?.token,
          atmid: clientcode,
          startdate: date,
          enddate: formatDateToYYYYMMDD(addDays(convertToDate(date || '') || new Date(), 3))
        }

        const { data } = await axios.get(`e2eservice`, { params })

        if (data.errorcode !== 0) throw new Error(data.message)

        const nestedServices = data.Object || data.object

        if (!nestedServices || nestedServices.length === 0) {
          toast.error(t("sip.o_e2e.toast_no_detail"))
          return null
        }

        const flattenedServices = nestedServices.flatMap((item: any) => item.laE2e || [])

        let dateFormatted = ''
        if (date) {
          dateFormatted = format((convertToDate(date) || new Date()), "yyyy-MM-dd")
        }

        const result = flattenedServices.filter((s: any) => {
          const serviceDate = format(new Date(s.m_DATUM), "yyyy-MM-dd")
          return serviceDate === dateFormatted
            && s.m_ClientID === clientcode
            && String(s.m_RELNR) === String(relnr)
        })

        if (result.length === 0) {
          toast.error(t("sip.o_e2e.toast_no_detail"))
          return null
        }

        return result
      } catch {
        return null
      }
    }
  })

  const { data: document, isFetching: isFetching_document } = useQuery<string[] | null>({
    queryKey: [`e2e_document_${clientcode}_${date}_${relnr}`],
    queryFn: async () => {
      try {
        const interventionCode = detail?.find((s: any) => s.m_Intervention_Code)?.m_Intervention_Code
        if (!interventionCode) return null

        toast.loading(t("sip.o_e2e.toast_loading_rapport"), { id: 'e2eDocument' })
        const { data } = await axios.post(`intervention/document`, {
          token: auth.data?.token,
          code: interventionCode
        })
        if (data.errorcode !== 0) throw new Error(data.message)
        toast.remove('e2eDocument')
        if (!data.object.filename) return null
        return data.object.filename
      } catch {
        toast.error(t("sip.o_e2e.toast_error_rapport"), { id: 'e2eDocument' })
        return null
      }
    },
    enabled: !!detail,
    refetchOnWindowFocus: false
  })

  const relation = customers.find(obj => obj.eindklant.some(e => String(e.relnr) === String(relnr)))
  const partner = partners.find(obj => obj.relnr === relation?.id)
  const customer = customers.flatMap(obj => obj.eindklant).find(e => String(e.relnr) === String(relnr))

  useEffect(() => {
    if (!detail) {
      const instance = lottie.loadAnimation({
        container: errorContainer.current,
        renderer: 'svg',
        loop: true,
        autoplay: true,
        animationData: errorAnimation
      })
      return () => instance.destroy()
    }
  }, [detail])

  const typeMapping: Record<string, string> = {
    'A_SwapIn': t("sip.o_e2e.swap_in"),
    'E_SwapOut': t("sip.o_e2e.swap_out"),
    'D_Retract': t("sip.o_e2e.retract"),
    'B_Counters': t("sip.o_e2e.counters"),
    'C_RejectNOK': t("sip.o_e2e.reject"),
    'J_RejectNok': t("sip.o_e2e.reject_nok"),
    'F_New Amount': t("sip.o_e2e.new_amount"),
    'I_Remaining': t("sip.o_e2e.remaining"),
  }

  const groupServicesByType = (services: any[]): E2EDetailRow[] => {
    const grouped: Record<string, E2EDetailRow> = {}
    const typesWithData = new Set<string>()

    TYPE_ORDER.forEach(type => {
      grouped[`__${type}`] = {
        type: typeMapping[type] || type,
        keepsafe: '',
        eur5: 0, eur10: 0, eur20: 0, eur50: 0,
        eur100: 0, eur200: 0, eur500: 0, total: 0
      }
    })

    services.forEach(service => {
      const type = service.m_IdentifierType
      const keepsafe = service.m_KEEPSAFE || ''
      const key = `${type}_${keepsafe}`

      if (!grouped[key]) {
        grouped[key] = {
          type: typeMapping[type] || type,
          keepsafe,
          eur5: 0, eur10: 0, eur20: 0, eur50: 0,
          eur100: 0, eur200: 0, eur500: 0, total: 0
        }
      }

      typesWithData.add(type)

      const qty = service.m_QTY || 0
      switch (service.m_COUPURE) {
        case 5:   grouped[key].eur5   += qty; break
        case 10:  grouped[key].eur10  += qty; break
        case 20:  grouped[key].eur20  += qty; break
        case 50:  grouped[key].eur50  += qty; break
        case 100: grouped[key].eur100 += qty; break
        case 200: grouped[key].eur200 += qty; break
        case 500: grouped[key].eur500 += qty; break
      }
    })

    TYPE_ORDER.forEach(type => {
      if (typesWithData.has(type)) delete grouped[`__${type}`]
    })

    const rows = Object.values(grouped).map(row => ({
      ...row,
      total: row.eur5 * 5 + row.eur10 * 10 + row.eur20 * 20 + row.eur50 * 50
        + row.eur100 * 100 + row.eur200 * 200 + row.eur500 * 500
    }))

    return rows.sort((a, b) => {
      const keyA = Object.keys(typeMapping).find(k => typeMapping[k] === a.type) ?? ''
      const keyB = Object.keys(typeMapping).find(k => typeMapping[k] === b.type) ?? ''
      return TYPE_ORDER.indexOf(keyA) - TYPE_ORDER.indexOf(keyB)
    })
  }

  const DetailTab = () => {
    if (!detail) return null
    const rows = groupServicesByType(detail)

    return (
      <div className='rounded-sm overflow-x-auto'>
        <Table>
          <TableHeader className='bg-slate-100 relative'>
            <TableRow>
              <TableHead className='border uppercase'>{t("sip.o_e2e.type")}</TableHead>
              <TableHead className='border uppercase'>{t("sip.o_e2e.keepsafe")}</TableHead>
              <TableHead className='border uppercase text-right'>{t("sip.o_e2e.count")} €5</TableHead>
              <TableHead className='border uppercase text-right'>{t("sip.o_e2e.count")} €10</TableHead>
              <TableHead className='border uppercase text-right'>{t("sip.o_e2e.count")} €20</TableHead>
              <TableHead className='border uppercase text-right'>{t("sip.o_e2e.count")} €50</TableHead>
              <TableHead className='border uppercase text-right'>{t("sip.o_e2e.count")} €100</TableHead>
              <TableHead className='border uppercase text-right'>{t("sip.o_e2e.count")} €200</TableHead>
              <TableHead className='border uppercase text-right'>{t("sip.o_e2e.count")} €500</TableHead>
              <TableHead className='border uppercase text-right'>{t("sip.o_e2e.total")} €</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody className='text-sm'>
            {rows.map((row, index) => (
              <TableRow className={`${index % 2 && 'bg-red-50'}`} key={`${row.type}_${row.keepsafe}`}>
                <TableCell className='p-3 border whitespace-nowrap capitalize'>{row.type}</TableCell>
                <TableCell className='p-3 border'>{row.keepsafe || '-'}</TableCell>
                <TableCell className='p-3 border text-right'>{row.eur5}</TableCell>
                <TableCell className='p-3 border text-right'>{row.eur10}</TableCell>
                <TableCell className='p-3 border text-right'>{row.eur20}</TableCell>
                <TableCell className='p-3 border text-right'>{row.eur50}</TableCell>
                <TableCell className='p-3 border text-right'>{row.eur100}</TableCell>
                <TableCell className='p-3 border text-right'>{row.eur200}</TableCell>
                <TableCell className='p-3 border text-right'>{row.eur500}</TableCell>
                <TableCell className='p-3 border text-right font-semibold'>{formatCashAmount(row.total)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    )
  }

  const RapportTab = () => (
    <div className='flex flex-col gap-4'>
       {!document && isFetching_document && <p>{t("sip.o_e2e.loading")}</p>}
    {!document && !isFetching_document && <p>{t("sip.o_e2e.no_rapport_available")}</p>}
      {document && (
        <>
          {document.length > 1 && (
            <div className='flex gap-2'>
              <button
                disabled={selected_document === 0}
                onClick={() => setselected_document(prev => prev - 1)}
                className='border rounded-full h-10 w-10 flex justify-center items-center text-neutral-500 hover:text-primary hover:bg-red-50 duration-100 disabled:opacity-50 disabled:cursor-not-allowed'
              >
                <ChevronLeft className='w-5' />
              </button>
              <button
                disabled={selected_document === document.length - 1}
                onClick={() => setselected_document(prev => prev + 1)}
                className='border rounded-full h-10 w-10 flex justify-center items-center text-neutral-500 hover:text-primary hover:bg-red-50 duration-100 disabled:opacity-50 disabled:cursor-not-allowed'
              >
                <ChevronRight className='w-5' />
              </button>
            </div>
          )}
          <div className='min-h-[500px] overflow-hidden border rounded-sm relative'>
            {['jpg', 'jpeg', 'png'].some(ext => document[selected_document].toLowerCase().includes(ext))
              ? <img src={document[selected_document]} />
              : (
                <object className='min-h-[500px] h-full w-full' data={document[selected_document]} type='application/pdf'>
                  <p>{t("sip.o_e2e.no_preview")}</p>
                  <Link to={document[selected_document]}><Button>{t("sip.o_e2e.download")}</Button></Link>
                </object>
              )
            }
          </div>
        </>
      )}
    </div>
  )

  return (
    <>
      <Toaster />

      {isFetching && !isInterface(detail, [] as any[]) && <LoadingScreen />}

      {detail === null && (
        <div className='flex flex-col items-center justify-center'>
          <div className='w-[240px] h-[240px]' ref={errorContainer}></div>
          <h2 className='text-2xl font-semibold mb-4'>{t("sip.o_e2e.error_title")}</h2>
          <p className='text-center'>
            {t("sip.o_e2e.error_description")}<br />
            {t("sip.o_e2e.error_description_p2")}{' '}
            <Link to='/sip/beheer-e2e' className='font-medium underline'>
              {t("sip.o_e2e.error_description_p3")}
            </Link>{' '}
            {t("sip.o_e2e.error_description_p4")}
          </p>
        </div>
      )}

      {isInterface(detail, [] as any[]) && (
        <div className="overflow-auto relative w-full p-4 md:p-8">
          <div className='flex flex-col'>
            <Breadcrumb
              items={[
                { title: <p onClick={() => navigate('/sip')} className='cursor-pointer'>{t("sip.o_e2e.link_sip")}</p> },
                { title: <p onClick={() => navigate('/sip/beheer-e2e')} className='cursor-pointer'>{t("sip.o_e2e.link_e2e")}</p> },
                { title: `${t("sip.o_e2e.link_detail")} ${clientcode}` }
              ]}
            />
            <h1 className='text-2xl font-semibold'>{t("sip.o_e2e.title_detail")}</h1>
          </div>

          <div className='flex border flex-col gap-4 rounded-sm p-4 mt-4 md:mt-8'>
            <h2 className='text-lg'>{t("sip.o_e2e.title_detail")}</h2>
            <div className='grid grid-cols-[repeat(auto-fit,minmax(250px,1fr))] gap-2'>
              <div className='flex gap-2'>
                <p className='font-semibold'>{t("sip.o_e2e.partner")}</p>
                <p className='text-neutral-500'>{partner?.name}</p>
              </div>
              <div className='flex gap-2'>
                <p className='font-semibold'>{t("sip.o_e2e.customer")}</p>
                <p className='text-neutral-500'>{customer?.name}</p>
              </div>
              <div className='flex gap-2'>
                <p className='font-semibold'>{t("sip.o_e2e.atm_id")}</p>
                <p className='text-neutral-500'>{clientcode}</p>
              </div>
              <div className='flex gap-2'>
                <p className='font-semibold'>{t("sip.o_e2e.date")}</p>
                <p className='text-neutral-500'>{format(convertToDate(date || '') || new Date(), "dd/MM/yyyy")}</p>
              </div>
            </div>
          </div>

          <div className='flex border flex-col rounded-sm pt-3 mt-4 md:mt-8'>
          <Tablist value={selected_subpage} onValueChange={(v) => setselected_subpage(v)}>
  <TablistItem value='d'>{t("sip.o_e2e.detail")}</TablistItem>
  <TablistItem value='r'>{t("sip.o_e2e.rapport")}</TablistItem>
</Tablist>

            <div className='p-4'>
              {selected_subpage === 'd' && <DetailTab />}
              {selected_subpage === 'r' && <RapportTab />}
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default Detail